import { LocalizedText, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { getSafeNotificationRoute } from "./notificationRoutes";
import { useNotifications } from "./useNotifications";
import "./NotificationCenter.css";
import { ACTIVITY_TYPES, activityType, requiresAction } from "./activityPresentation";

const PREVIEW_LIMIT = 5;

function displayCount(value) {
  const count = Math.max(0, Number(value) || 0);
  return count > 99 ? "99+" : String(count);
}

function isRead(item) {
  return Boolean(item?.isRead ?? item?.is_read ?? item?.readAt ?? item?.read_at);
}

function isSeen(item) {
  return Boolean(item?.isSeen ?? item?.is_seen ?? item?.seenAt ?? item?.seen_at);
}

export default function NotificationBellPopover({ icon }) {
  useSourceLanguage();
  const location = useLocation();
  const {
    previewItems = [],
    unreadCount = 0,
    listStatus = "idle",
    listError = "",
    countError = "",
    markSeen,
    patchNotification,
    bulkAction,
    refresh,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const [actionError, setActionError] = useState("");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const displayedItems = useMemo(() => previewItems.slice(0, PREVIEW_LIMIT), [previewItems]);
  const labelledCount = Math.max(0, Number(unreadCount) || 0);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return undefined;

    const unseenIds = displayedItems
      .filter((item) => !isSeen(item))
      .map((item) => String(item.id || item.publicId || ""))
      .filter(Boolean);
    if (unseenIds.length) {
      markSeen(unseenIds).catch((error) => {
        setActionError(error?.message || "L’état vu n’a pas pu être synchronisé.");
      });
    }

    const frame = window.requestAnimationFrame(() => {
      popoverRef.current
        ?.querySelector("a[href], button:not([disabled])")
        ?.focus();
    });

    const closeFromKeyboard = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const closeFromOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", closeFromKeyboard);
    document.addEventListener("pointerdown", closeFromOutside);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", closeFromKeyboard);
      document.removeEventListener("pointerdown", closeFromOutside);
    };
  }, [displayedItems, markSeen, open]);

  const markAllRead = async () => {
    setActionError("");
    try {
      await bulkAction("mark_all_read");
    } catch (error) {
      setActionError(error?.message || "Les notifications n’ont pas pu être marquées comme lues.");
    }
  };

  const openNotification = (item) => {
    setOpen(false);
    if (!isRead(item)) {
      patchNotification(item.id || item.publicId, "mark_read").catch(() => {
        // La destination reste accessible ; le prochain refresh réconciliera l’état serveur.
      });
    }
  };

  return (
    <div className="notification-bell" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="et-global-notifications notification-bell__trigger"
        aria-label={`Notifications${labelledCount ? `, ${labelledCount} non lue${labelledCount > 1 ? "s" : ""}` : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="notification-bell-popover"
        onClick={() => {
          setActionError("");
          setOpen((current) => !current);
        }}
      >
        <span aria-hidden="true">{icon || "🔔"}</span>
        {labelledCount > 0 ? (
          <b className="notification-bell__count" aria-hidden="true">{displayCount(labelledCount)}</b>
        ) : null}
      </button>

      {open ? (
        <section
          ref={popoverRef}
          id="notification-bell-popover"
          className="notification-bell__popover"
          role="dialog"
          aria-modal="false"
          aria-labelledby="notification-bell-title"
        >
          <header className="notification-bell__heading">
            <h2 id="notification-bell-title"><LocalizedText source={"Notifications"} /></h2>
            <button type="button" disabled={!labelledCount} onClick={markAllRead}><LocalizedText source={"Tout marquer comme lu"} /></button>
          </header>

          {actionError ? <p className="notification-center__notice" role="alert">{actionError}</p> : null}
          {countError && !actionError ? <p className="notification-center__notice" role="alert">{countError}</p> : null}

          {listStatus === "loading" && !displayedItems.length ? (
            <p className="notification-bell__state" role="status"><LocalizedText source={"Chargement…"} /></p>
          ) : listError && !displayedItems.length ? (
            <div className="notification-bell__state" role="alert">
              <p>{listError}</p>
              <button type="button" onClick={() => refresh({ includeList: true })}><LocalizedText source={"Réessayer"} /></button>
            </div>
          ) : displayedItems.length ? (
            <ul className="notification-bell__list">
              {displayedItems.map((item) => {
                const id = String(item.id || item.publicId || "");
                const destination = item.available === false
                  ? ""
                  : getSafeNotificationRoute(item.link || "", "");
                const content = (
                  <>
                    <span>{ACTIVITY_TYPES.find((type) => type.id === activityType(item))?.label} · {isRead(item) ? <LocalizedText source={"Lue"} /> : <LocalizedText source={"Non lue"} />}{requiresAction(item) ? <LocalizedText source={" · Action requise"} /> : ""}</span>
                    <strong>{String(item.title || "Notification")}</strong>
                    {item.message ? <span>{String(item.message)}</span> : null}
                    {item.available === false ? <span><LocalizedText source={"Contenu indisponible"} /></span> : null}
                  </>
                );
                return (
                  <li key={id} className={`notification-bell__item${isRead(item) ? "" : " is-unread"}`}>
                    {destination ? (
                      <Link to={destination} onClick={() => openNotification(item)}>
                        {content}
                      </Link>
                    ) : (
                      <div aria-disabled="true">{content}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="notification-bell__state" role="status"><LocalizedText source={"Aucune notification récente."} /></p>
          )}

          <footer className="notification-bell__footer">
            <Link to="/mes-eternua/notifications" onClick={() => setOpen(false)}><LocalizedText source={"Voir toutes les notifications"} /></Link>
            <Link to="/mes-eternua/notifications/preferences" onClick={() => setOpen(false)}><LocalizedText source={"Préférences"} /></Link>
          </footer>
        </section>
      ) : null}
    </div>
  );
}
